/**
 * Página MOBILE "PnL" (#25a, Fase 2; formato v2 no #345-mobile). READ-ONLY.
 * Spec v1: docs/mocks/mobile_pnl_mock.html. Primeira dobra = 3 seções SEM rolar.
 *
 * v2 (alinhada ao report Telegram dos sócios, #345 — Telegram e app NÃO podem
 * se contradizer na cara do sócio):
 * 1. Resultado: cabeçalho = "Fechamento <as-of>" (a data que os números
 *    REPRESENTAM, não a de hoje); PONTE explícita Nominal + correção IPCA =
 *    Indexado com a conta FECHANDO na exibição (`pontePnl` — espelho TS do
 *    plug do #345; IPCA pendente → "pendente", NUNCA soma 0);
 *    "Resultado do dia (<as-of>)" (era "Δ Diário"/"hoje"); Δ S/M/A nominais
 *    com VF (principal) + VP (menor, chip −%); aviso "curva N dias úteis
 *    atrasada" SÓ quando `curva_stale` do backend (>1 du — mesma régua do
 *    Telegram, zero heurística local de dia útil, CX-INV-02).
 * 2. Curva curta resumida: /api/curva-pnl (4 vértices; D0 + Δ vs D-1);
 *    "ver completa" EXPANDE INLINE (6 vértices + total da longa) — controle
 *    vivo, não botão morto (#246).
 * 3. Posição líquida: /api/posicao-decomposta (#34) — net do 1º vértice +
 *    mini-barra empilhada por submercado COM SINAL + total MWh até 2035;
 *    "matriz" EXPANDE INLINE. SEM "MtM da posição" (v2: não é número de
 *    sócio — vive nas telas de risco do desktop).
 *
 * HONESTIDADES: loading "…" / ErroNote por seção; NUNCA mock (sem dado real
 * → "—"); alvos de toque ≥44px.
 */
import { useMemo, useState } from "react";
import type { PosicaoDecompLinha } from "../../api/types";
import { useCurvaPnl } from "../../hooks/useCurvaPnl";
import { useCurvaPnlSerie } from "../../hooks/useCurvaPnlSerie";
import { useDv01Ipca } from "../../hooks/useDv01Ipca";
import { usePnlDeltaAnual } from "../../hooks/usePnlDeltaAnual";
import { parseUtcNaive } from "../../utils/dateBr";
import { usePosicaoDecomposta } from "../../hooks/usePosicaoDecomposta";
import { useZeusStatus } from "../../hooks/useZeusStatus";
import { combinar, descontoChip } from "../mesa/ResultadoBookBrasil";
import { brlPonteSolto, pontePnl } from "../../utils/pnlPonte";
import { fmtBRLcompactSigned } from "../../utils/formatters";
import { brDate, numBr } from "../../format";
import { Card, ErroNote, MobileHeader } from "./mobileUi";
import type { AnotacaoCorrecao } from "../../api/types";

const ROTULO_JANELA: Record<string, string> = {
  diario: "Diário", semanal: "Semanal", mensal: "Mensal", anual: "Anual",
  diario_ipca: "Diário IPCA", semanal_ipca: "Semanal IPCA",
  mensal_ipca: "Mensal IPCA", anual_ipca: "Anual IPCA",
};
const ORDEM_JANELAS = ["diario", "semanal", "mensal", "diario_ipca", "semanal_ipca", "mensal_ipca"];

/** 28/09 (#461-mobile): anotação de CORREÇÃO — a MESMA regra e o MESMO conteúdo
 * da nota do card desktop (NotaAnotacoesCorrecao): 1 bloco por linha anotada
 * DENTRO da janela da série (~30d, mesma fonte /api/curva-pnl/serie); causa +
 * os 3 Δ corretos com VF E VP + "Anual não afetado" + "valor gravado mantido".
 * Gatilho = a API trouxe a anotação (estado), nunca data. Formatador do mobile
 * (brlPonteSolto) — paridade com o Telegram. NADA cortado. */
function NotaAnotacoesMobile({ anotacoes }: { anotacoes: AnotacaoCorrecao[] }) {
  if (anotacoes.length === 0) return null;
  return (
    <div className="mt-2 flex flex-col gap-2" data-testid="mobile-pnl-anotacoes">
      {anotacoes.map((a) => {
        const naoAfet = a.nao_afetados.map((j) => ROTULO_JANELA[j] ?? j).join(" e ");
        const janelas = ORDEM_JANELAS.filter((j) => a.valores[j]);
        if (janelas.length === 0) {
          // 13/10: sem par gravado×correto (troca de população da ponta IPCA).
          return (
            <div
              key={a.data_referencia}
              className="rounded border border-amber-400/30 bg-amber-400/5 px-2 py-1.5 text-[11px] leading-snug text-amber-200/90"
              data-testid={`mobile-pnl-anotacao-${a.data_referencia}`}
            >
              ⚠ {ddmm(a.data_referencia)} (fech. {ddmm(a.as_of)}): {a.causa} ({a.referencia}).
            </div>
          );
        }
        return (
          <div
            key={a.data_referencia}
            className="rounded border border-amber-400/30 bg-amber-400/5 px-2 py-1.5 text-[11px] leading-snug text-amber-200/90"
            data-testid={`mobile-pnl-anotacao-${a.data_referencia}`}
          >
            <p>
              ⚠ {ddmm(a.data_referencia)} (fech. {ddmm(a.as_of)}): Δ gravados com{" "}
              {a.causa_curta} ({a.referencia}). Corretos:
            </p>
            {janelas.map((j) => {
              const v = a.valores[j];
              return (
                <div
                  key={j}
                  className="flex justify-between font-mono tabular-nums"
                  data-testid={`mobile-pnl-anotacao-${a.data_referencia}-${j}`}
                >
                  <span className="font-sans">{ROTULO_JANELA[j]}</span>
                  <span>
                    {brlPonteSolto(v.correto_vf)}
                    <span className="text-amber-200/60 ml-2">
                      VP {v.correto_vp !== null ? brlPonteSolto(v.correto_vp) : "—"}
                    </span>
                  </span>
                </div>
              );
            })}
            <p className="text-amber-200/70">
              {naoAfet ? `${naoAfet} não afetado. ` : ""}Valor gravado mantido.
            </p>
          </div>
        );
      })}
    </div>
  );
}

// Cores por submercado (as do card desktop #34 / mock).
const COR_SUB: Record<string, string> = {
  SE: "#00dac8", S: "#4f8cff", NE: "#f0b429", N: "#b06bff",
};

function corValor(v: number): string {
  if (Math.abs(v) < 0.005) return "text-white/40";
  return v >= 0 ? "text-indra-positivo" : "text-indra-negativo";
}

/** "Zeus 10:05 · 07/07" do primary do last-sync; null = sem as-of (omite).
 * F-25a-1: finished_at vem UTC NAIVE do backend → parseUtcNaive (senão a
 * hora UTC aparece rotulada como local, +3h no BRT).
 * Exportada pro Mobile6du (#25b) — mesmo as-of, zero re-derivação. */
export function zeusAsOf(finishedAt: string | null | undefined): string | null {
  if (!finishedAt) return null;
  const d = parseUtcNaive(finishedAt);
  if (Number.isNaN(d.getTime())) return null;
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const mo = String(d.getMonth() + 1).padStart(2, "0");
  return `Zeus ${hh}:${mm} · ${dd}/${mo}`;
}

/** Grupos por submercado do vértice, NET com sinal, ordem fixa. */
function gruposSub(l: PosicaoDecompLinha, ordem: string[]) {
  const acc = new Map<string, number>();
  for (const c of l.celulas) acc.set(c.submercado, (acc.get(c.submercado) ?? 0) + c.mwm);
  return [...acc.entries()]
    .map(([sub, mwm]) => ({ sub, mwm }))
    .sort((a, b) => ordem.indexOf(a.sub) - ordem.indexOf(b.sub));
}

/** ISO "2026-07-22" → "22/07" (data civil, sem timezone). */
function ddmm(iso: string | null | undefined): string | null {
  const m = iso ? /^(\d{4})-(\d{2})-(\d{2})/.exec(iso) : null;
  return m ? `${m[3]}/${m[2]}` : null;
}

export function MobilePnl({ onLogout, onRefresh }: {
  onLogout: () => void;
  onRefresh?: () => void;
}) {
  const curvas = useCurvaPnl();
  // 28/09: anotações de correção da janela (mesma fonte/regra do card desktop).
  const serie = useCurvaPnlSerie(30);
  const dv01 = useDv01Ipca();
  const decomp = usePosicaoDecomposta(null, null);
  // #40: staleness da curva vem do BACKEND (mesma régua do Telegram/#345:
  // `curva_stale` = as-of >= 2 du) — NUNCA heurística local de dia útil (CX-INV-02).
  const curvaDs = useZeusStatus().data?.datasets.curva_forward ?? null;

  const [curvaAberta, setCurvaAberta] = useState(false);
  const [matrizAberta, setMatrizAberta] = useState(false);

  // --- 1. Resultado (formato v2 — espelho do report Telegram #345) ---
  const res = curvas.data.resultado;
  const dvTotal = dv01.data?.dv01_curva_total ?? null;
  const deltaAnual = usePnlDeltaAnual();
  const dad = deltaAnual.data;
  const temFoto = dad?.metadados.tem_foto ?? false;
  // PONTE do mês (indexadoDe/pontePnl — MESMA soma do Telegram e do CX-INV-13):
  // null = componente ausente (IPCA pendente OU nominal indisponível) →
  // renderiza a pendência no lado certo, NÃO soma.
  const ipcaMensal = res.fv_mensal_ipca ?? null;
  const ponte = pontePnl(res.fv_mensal, ipcaMensal);
  // Demais janelas na lente NOMINAL (a ponte já conta a história do indexado).
  const dWin = combinar(false, res.fv_diario, res.pv_diario, res.fv_diario_ipca, res.pv_diario_ipca, false);
  const sWin = combinar(false, res.fv_semanal, res.pv_semanal, res.fv_semanal_ipca, res.pv_semanal_ipca, false);
  const mWin = combinar(false, res.fv_mensal, res.pv_mensal, res.fv_mensal_ipca, res.pv_mensal_ipca, false);
  const aWin = combinar(
    false,
    temFoto ? dad!.delta_anual_vf : null,
    temFoto ? dad!.delta_anual_vp : null,
    dad?.delta_anual_ipca_vf, dad?.delta_anual_ipca_vp,
    false,
  );
  const fechamento = ddmm(res.as_of);
  const linhasDelta = [
    // v2: "Resultado do dia" (não "movimento"/"hoje"), rotulado com a DATA do
    // fechamento — curva parada ⇒ o sócio VÊ que é o mesmo fechamento.
    { key: "diario", label: `Resultado do dia${fechamento ? ` (${fechamento})` : ""}`, win: dWin },
    { key: "semanal", label: "Δ Semanal", win: sWin },
    // Δ Mensal repete o nominal da ponte na unidade PROPRIA do valor (mesmo
    // formatador brlPonteSolto): complementa a precisao quando a unidade unica
    // da ponte achata um componente pequeno (ex. ponte "R$ 0,0 mi" ↔ row
    // "-R$ 49 mil") — e traz o VP, que a ponte nao tem.
    { key: "mensal", label: "Δ Mensal", win: mWin },
    { key: "anual", label: "Δ Anual", win: aWin, aguardando: !temFoto },
  ];

  // --- 2. Curva curta (só dado REAL — nunca o mock do desktop) ---
  const curtaReal = !curvas.isLoading && !curvas.error ? curvas.data.curta.linhas : [];
  const curtaRows = curvaAberta ? curtaReal : curtaReal.slice(0, 4);

  // --- 3. Posição (1º vértice + total até 2035) ---
  const vert0 = decomp.data?.curta.linhas[0] ?? null;
  const grupos = useMemo(
    () => (vert0 && decomp.data ? gruposSub(vert0, decomp.data.dimensoes.submercados) : []),
    [vert0, decomp.data],
  );
  const totalMwh = decomp.data
    // GO 03/08: mês fora da cobertura (mwh null) não soma aqui — o volume
    // dele já está no vértice anual dentro de longa.total_mwh (sem furo).
    ? decomp.data.curta.linhas.reduce((s, l) => s + (l.mwh ?? 0), 0) + decomp.data.longa.total_mwh
    : null;
  const direcao = vert0 && vert0.mwm !== null
    ? Math.abs(vert0.mwm) < 0.05 ? "casado" : vert0.mwm > 0 ? "net comprado" : "net vendido"
    : null;
  const maxLado = useMemo(() => {
    if (!grupos.length) return 1;
    return Math.max(
      Math.abs(grupos.filter((g) => g.mwm < 0).reduce((s, g) => s + g.mwm, 0)),
      grupos.filter((g) => g.mwm > 0).reduce((s, g) => s + g.mwm, 0),
      0.01,
    );
  }, [grupos]);

  return (
    <div className="min-h-screen bg-indra-azul" data-testid="mobile-pnl">
      <MobileHeader subtitle="PnL" onLogout={onLogout} onRefresh={onRefresh} />
      <main className="px-4 py-4 flex flex-col gap-4 max-w-md mx-auto">
        {/* v2: o cabeçalho é a DATA QUE OS NÚMEROS REPRESENTAM (as-of do
            fechamento da curva), não a data de hoje — mesma régua do Telegram. */}
        {fechamento && (
          <p className="text-[11px] text-indra-turquesa/80 -mb-2" data-testid="mobile-pnl-fechamento">
            Fechamento {fechamento}
          </p>
        )}
        {/* Aviso SÓ quando a as-of está mais velha que o D-1 normal (backend
            `curva_stale`, >1 du — nunca no D-1 legítimo de toda manhã). */}
        {curvaDs?.curva_stale && (
          <p className="text-[11px] text-red-300/90 -mb-2" data-testid="mobile-pnl-curva-atrasada">
            ⚠ Curva {curvaDs.defasagem_dias_uteis != null
              ? `${curvaDs.defasagem_dias_uteis} dias úteis atrasada`
              : "desatualizada"} — este é o último fechamento disponível.
          </p>
        )}

        {/* 1 · RESULTADO — a PONTE do mês (formato v2, espelho do Telegram):
            Nominal + correção IPCA = Indexado, com a conta FECHANDO. */}
        <Card title="Resultado no mês" testId="mobile-pnl-resultado">
          {curvas.error ? (
            <ErroNote testId="mobile-pnl-resultado-erro" />
          ) : (
            <>
              {curvas.isLoading ? (
                <div className="text-white/40 text-sm">…</div>
              ) : (
                <div className="font-mono tabular-nums" data-testid="mobile-pnl-ponte">
                  {/* pendencia rotulada no LADO CERTO (paridade com o fallback do
                      Telegram/#345): sem IPCA → "pendente"/"aguardando IPCA"; sem
                      NOMINAL (ex. pre-dado) → e o nominal que esta indisponivel. */}
                  <div className="flex items-baseline justify-between py-0.5">
                    <span className="text-xs text-white/60">Nominal</span>
                    <span
                      className={`text-sm font-semibold ${ponte ? corValor(ponte.nominalQ) : res.fv_mensal !== null ? corValor(res.fv_mensal) : "text-white/40"}`}
                      data-testid="mobile-pnl-ponte-nominal"
                    >
                      {ponte ? ponte.nominal : res.fv_mensal !== null ? brlPonteSolto(res.fv_mensal) : "—"}
                    </span>
                  </div>
                  <div className="flex items-baseline justify-between py-0.5">
                    <span className="text-xs text-white/60">+ correção IPCA</span>
                    <span
                      className={`text-sm ${ponte ? corValor(ponte.ipcaQ) : ipcaMensal !== null ? "text-white/80" : "text-amber-400/80"}`}
                      data-testid="mobile-pnl-ponte-ipca"
                    >
                      {ponte ? ponte.ipca : ipcaMensal !== null ? brlPonteSolto(ipcaMensal) : "pendente"}
                    </span>
                  </div>
                  <div className="flex items-baseline justify-between py-0.5 border-t border-white/10 mt-0.5 pt-1">
                    <span className="text-xs text-white/80 font-semibold">= Indexado</span>
                    <span
                      className={`text-[24px] font-black font-sans ${ponte ? corValor(ponte.indexadoQ) : "text-white/40"}`}
                      data-testid="mobile-pnl-ponte-indexado"
                    >
                      {ponte ? ponte.indexado
                        : ipcaMensal !== null ? "— (nominal indisponível)" : "— (aguardando IPCA)"}
                    </span>
                  </div>
                </div>
              )}

              {/* Resultado do dia + Δ S/M/A — VF principal + VP menor com chip −%.
                  Formatador UNICO do card = o da ponte (brlPonteSolto, paridade
                  com o _brl_solto do Telegram): "+R$ 2,0 mi" (nunca "+R$ 2 mi"),
                  fronteira 999.600→"1,0 mi", sinal ASCII — review #346 F1-F3. */}
              <div className="mt-2" data-testid="mobile-pnl-deltas">
                {linhasDelta.map((l) => (
                  <div key={l.key} className="flex items-baseline justify-between py-1 border-b border-white/5 last:border-0">
                    <span className="text-xs text-white/60">{l.label}</span>
                    {l.aguardando ? (
                      <span className="text-[11px] text-white/35 italic" data-testid={`mobile-pnl-delta-${l.key}-aguardando`}>
                        aguardando baseline (31/12)
                      </span>
                    ) : (
                      <span className="text-right font-mono tabular-nums" data-testid={`mobile-pnl-delta-${l.key}`}>
                        <b className={`text-sm ${l.win.fv !== null ? corValor(l.win.fv) : "text-white/40"}`}>
                          {l.win.fv !== null ? brlPonteSolto(l.win.fv) : "—"}
                        </b>
                        <span className="text-[11px] text-white/45 ml-2">
                          VP {l.win.pv !== null ? brlPonteSolto(l.win.pv) : "—"}
                          {descontoChip(l.win.fv, l.win.pv) ? ` ${descontoChip(l.win.fv, l.win.pv)}` : ""}
                        </span>
                      </span>
                    )}
                  </div>
                ))}
              </div>

              <NotaAnotacoesMobile anotacoes={serie.data.anotacoes ?? []} />

              <p className="text-[11px] text-white/45 mt-1.5" data-testid="mobile-pnl-sub">
                VF/VP{res.curva_pre_asof ? ` · curva-pré as-of ${brDate(res.curva_pre_asof).slice(0, 5)}` : ""}
                {" "}· DV01 {dvTotal !== null ? `${fmtBRLcompactSigned(dvTotal)}/bp` : "—"}
              </p>
            </>
          )}
        </Card>

        {/* 2 · CURVA CURTA resumida */}
        <Card title="Curva curta" testId="mobile-pnl-curva">
          {curvas.error ? (
            <ErroNote testId="mobile-pnl-curva-erro" />
          ) : curvas.isLoading ? (
            <div className="text-white/40 text-sm">…</div>
          ) : curtaReal.length === 0 ? (
            <div className="text-white/40 text-sm" data-testid="mobile-pnl-curva-vazio">—</div>
          ) : (
            <>
              <table className="w-full text-sm">
                <tbody data-testid="mobile-pnl-curva-tabela">
                  {curtaRows.map((l) => (
                    <tr key={l.vertice} className="border-b border-white/10 last:border-0">
                      <td className="py-1.5 text-white/80">{l.vertice}</td>
                      <td className="py-1.5 text-right font-mono tabular-nums text-white">
                        {l.precoD0 !== null ? numBr(l.precoD0, 0) : "—"}
                      </td>
                      <td className={`py-1.5 text-right font-mono tabular-nums text-xs ${l.delta === null ? "text-white/35" : corValor(l.delta)}`}>
                        {l.delta === null ? "—" : `${l.delta >= 0 ? "+" : "−"}${numBr(Math.abs(l.delta), 0)}`}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {curvaAberta && (
                <p className="text-[11px] text-white/50 mt-2" data-testid="mobile-pnl-longa-total">
                  Longa (anual até 2035): {numBr(curvas.data.longa.total_mwh, 0)} MWh
                </p>
              )}
              <div className="flex items-center justify-between mt-1">
                <p className="text-[11px] text-white/40">preço D0 · Δ vs D-1 · SE convencional</p>
                {/* controle VIVO: expande inline (não é navegação prometida) */}
                <button
                  type="button"
                  data-testid="mobile-pnl-curva-toggle"
                  aria-expanded={curvaAberta}
                  onClick={() => setCurvaAberta((v) => !v)}
                  className="min-h-11 px-2 text-xs text-indra-turquesa active:text-white"
                >
                  {curvaAberta ? "ver menos ‹" : "ver completa ›"}
                </button>
              </div>
            </>
          )}
        </Card>

        {/* 3 · POSIÇÃO LÍQUIDA */}
        <Card title="Posição líquida" testId="mobile-pnl-posicao">
          {decomp.error ? (
            <ErroNote testId="mobile-pnl-posicao-erro" />
          ) : decomp.isLoading ? (
            <div className="text-white/40 text-sm">…</div>
          ) : !vert0 ? (
            <div className="text-white/40 text-sm" data-testid="mobile-pnl-posicao-vazio">—</div>
          ) : (
            <>
              <div className={`text-[22px] font-black font-sans tabular-nums ${corValor(vert0.mwm ?? 0)}`} data-testid="mobile-pnl-mwm">
                {vert0.mwm === null ? "—" : `${numBr(vert0.mwm, 2)} MWm`}{" "}
                <span className="text-xs font-normal text-white/60">
                  {direcao} · {vert0.vertice}
                </span>
              </div>
              {/* mini-barra empilhada COM SINAL: vendido ◂ 0 ▸ comprado */}
              <div className="flex items-center h-[20px] mt-2" aria-hidden>
                <div className="flex-1 flex justify-end h-full rounded-l overflow-hidden bg-indra-azul-deep">
                  {grupos.filter((g) => g.mwm < 0).map((g) => (
                    <span
                      key={g.sub}
                      data-testid={`mobile-pnl-seg-neg-${g.sub}`}
                      style={{ width: `${(Math.abs(g.mwm) / maxLado) * 100}%`, background: COR_SUB[g.sub] ?? "#8ba6bf" }}
                      className="h-full block"
                    />
                  ))}
                </div>
                <div className="w-[2px] h-full bg-white/40" />
                <div className="flex-1 flex h-full rounded-r overflow-hidden bg-indra-azul-deep">
                  {grupos.filter((g) => g.mwm > 0).map((g) => (
                    <span
                      key={g.sub}
                      data-testid={`mobile-pnl-seg-pos-${g.sub}`}
                      style={{ width: `${(g.mwm / maxLado) * 100}%`, background: COR_SUB[g.sub] ?? "#8ba6bf" }}
                      className="h-full block"
                    />
                  ))}
                </div>
              </div>
              <div className="flex gap-3 flex-wrap text-[11px] text-white/60 mt-2" data-testid="mobile-pnl-legenda">
                {/* N-25a-2: grupo com net ~0 fora da legenda (zero não é comprado). */}
                {grupos.filter((g) => Math.abs(g.mwm) >= 0.05).map((g) => (
                  <span key={g.sub}>
                    <i className="inline-block w-[9px] h-[9px] rounded-[2px] mr-1 align-middle" style={{ background: COR_SUB[g.sub] ?? "#8ba6bf" }} />
                    {g.sub} {g.mwm >= 0 ? "+" : "−"}{numBr(Math.abs(g.mwm), 1)}
                    {g.mwm > 0 ? " (comprado)" : ""}
                  </span>
                ))}
              </div>
              {/* v2 (#345-mobile): SEM "MtM da posição" — não é número de sócio;
                  o MtM vive nas telas de risco do desktop (Exposição). */}
              <div className="flex items-center justify-between mt-1">
                <p className="text-[11px] text-white/50" data-testid="mobile-pnl-total-mwh">
                  {totalMwh !== null ? `total ${numBr(totalMwh, 0)} MWh (até 2035)` : ""}
                </p>
                <button
                  type="button"
                  data-testid="mobile-pnl-matriz-toggle"
                  aria-expanded={matrizAberta}
                  onClick={() => setMatrizAberta((v) => !v)}
                  className="min-h-11 px-2 text-xs text-indra-turquesa active:text-white"
                >
                  {matrizAberta ? "fechar ‹" : "matriz ›"}
                </button>
              </div>
              {matrizAberta && (
                <div className="mt-1 border-t border-white/10 pt-2" data-testid="mobile-pnl-matriz">
                  {vert0.celulas.map((c) => (
                    <div key={`${c.tipo_energia}|${c.submercado}`} className="flex justify-between text-xs py-1">
                      <span className="text-white/60">{c.tipo_energia} · {c.submercado}</span>
                      <span className={`font-mono tabular-nums ${corValor(c.mwm)}`}>{numBr(c.mwm, 2)} MWm</span>
                    </div>
                  ))}
                  <div className="flex justify-between text-xs py-1 border-t border-white/10 font-semibold">
                    <span className="text-white">Total {vert0.vertice}</span>
                    {/* GO 03/08: null = fora da cobertura do feed → "—" */}
                    <span className={`font-mono tabular-nums ${corValor(vert0.mwm ?? 0)}`}>
                      {vert0.mwm === null ? "—" : `${numBr(vert0.mwm, 2)} MWm`}
                    </span>
                  </div>
                </div>
              )}
            </>
          )}
        </Card>
      </main>
    </div>
  );
}
